import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  IconButton,
  Paper,
  CircularProgress,
  Skeleton
} from '@mui/material';
import {
  Settings as SettingsIcon,
  SportsSoccer as SportsIcon,
  Restore as RestoreIcon,
  CheckCircle as CheckIcon,
  ArrowBack as ArrowBackIcon,
  DragHandle as DragHandleIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Lock as LockIcon,
  Star as StarIcon
} from '@mui/icons-material';
import SportsbookPreferences from '../../services/sportsbookPreferences';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../../contexts/SettingsContext';

const SPORTSBOOKS = SportsbookPreferences.getDefaultSportsbooks();

const SettingsScreen = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { getSetting, setSetting } = useSettings();
  const [sportsbookOrder, setSportsbookOrder] = useState([]);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [exitConfirmDialogOpen, setExitConfirmDialogOpen] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Load sportsbook order from service on component mount
  useEffect(() => {
    loadSportsbookOrder();
  }, [currentUser]);

  const loadSportsbookOrder = async () => {
    try {
      setLoading(true);
      setError(null);
      const order = await SportsbookPreferences.getSportsbookOrder();
      setSportsbookOrder(order);
      setOriginalOrder([...order]);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error loading sportsbook order:', error);
      setError('Failed to load sportsbook preferences. Using default order.');
      const defaultOrder = [...SPORTSBOOKS];
      setSportsbookOrder(defaultOrder);
      setOriginalOrder([...defaultOrder]);
      setHasUnsavedChanges(false);
    } finally {
      setLoading(false);
    }
  };

  const updateSportsbookOrder = (newOrder) => {
    setSportsbookOrder(newOrder);
    // Check if order has changed from original
    const hasChanged = JSON.stringify(newOrder) !== JSON.stringify(originalOrder);
    setHasUnsavedChanges(hasChanged);
  };

  const saveSportsbookOrder = async (orderToSave = sportsbookOrder) => {
    try {
      setSaving(true);
      const success = await SportsbookPreferences.saveSportsbookOrder(orderToSave);
      if (success) {
        setOriginalOrder([...orderToSave]);
        setHasUnsavedChanges(false);
        setShowSaveSuccess(true);
        setTimeout(() => setShowSaveSuccess(false), 2000);
        return true;
      } else {
        setError('Failed to save sportsbook preferences.');
        return false;
      }
    } catch (error) {
      console.error('Error saving sportsbook order:', error);
      setError('Failed to save sportsbook preferences.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const moveSportsbook = (fromIndex, toIndex) => {
    // Prevent moving the top 2 entries (DraftKings and FanDuel)
    if (fromIndex < 2 || toIndex < 2) return;
    if (toIndex < 0 || toIndex >= sportsbookOrder.length) return;
    
    const newOrder = [...sportsbookOrder];
    const [movedItem] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, movedItem);
    
    updateSportsbookOrder(newOrder);
  };

  const moveUp = (index) => {
    // Prevent moving the top 2 entries or moving anything above position 2
    if (index < 2 || index - 1 < 2) return;
    moveSportsbook(index, index - 1);
  };

  const moveDown = (index) => {
    // Prevent moving the top 2 entries
    if (index < 2) return;
    moveSportsbook(index, index + 1);
  };

  const handleResetToDefaults = () => {
    const defaultOrder = [...SPORTSBOOKS];
    updateSportsbookOrder(defaultOrder);
    setResetDialogOpen(false);
  };

  const handleBackNavigation = () => {
    if (hasUnsavedChanges) {
      setExitConfirmDialogOpen(true);
    } else {
      navigate('/account');
    }
  };

  const handleSaveAndExit = async () => {
    const success = await saveSportsbookOrder();
    if (success) {
      setExitConfirmDialogOpen(false);
      navigate('/account');
    }
  };

  const handleDiscardAndExit = () => {
    setSportsbookOrder([...originalOrder]);
    setHasUnsavedChanges(false);
    setExitConfirmDialogOpen(false);
    navigate('/account');
  };

  const handleManualSave = async () => {
    await saveSportsbookOrder();
  };

  return (
    <Box sx={{ py: 2 }}>
      {/* Back Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton 
          onClick={handleBackNavigation}
          sx={{ mr: 1 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">
          Back to Account
        </Typography>
      </Box>

      {/* Header Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <SettingsIcon 
            sx={{ 
              fontSize: 60, 
              color: 'primary.main', 
              mb: 2 
            }} 
          />
          <Typography variant="h5" gutterBottom>
            Sportsbook Order
          </Typography>
          
          <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 2 }}>
            <Chip 
              icon={<CheckIcon />}
              label={`${sportsbookOrder.length} sportsbooks`}
              color="primary"
              size="small"
            />
            {hasUnsavedChanges && (
              <Chip 
                label="Unsaved changes"
                color="warning"
                size="small"
                variant="outlined"
              />
            )}
          </Stack>

          {/* Quick Actions */}
          <Stack direction="row" spacing={1} justifyContent="center">
            {hasUnsavedChanges && (
              <Button 
                size="small" 
                variant="contained"
                onClick={handleManualSave}
                startIcon={saving ? <CircularProgress size={16} /> : <CheckIcon />}
                disabled={saving || loading}
                color="primary"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            )}
            <Button 
              size="small" 
              variant="outlined"
              onClick={() => setResetDialogOpen(true)}
              startIcon={<RestoreIcon />}
              disabled={saving || loading}
            >
              Reset Order
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Success Alert */}
      {showSaveSuccess && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
          onClose={() => setShowSaveSuccess(false)}
        >
          Sportsbook order saved successfully!
          {currentUser ? ' Changes synced to your account.' : ' Changes saved locally.'}
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Sportsbooks List */}
      <Card>
        <CardContent sx={{ pb: 1 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <SportsIcon sx={{ mr: 1 }} />
            Sportsbook Order
            {saving && <CircularProgress size={16} sx={{ ml: 1 }} />}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Reorder sportsbooks for positions 3 and below. DraftKings and FanDuel are fixed at the top.
            {!currentUser && ' Note: Sign in to sync preferences across devices.'}
          </Typography>
        </CardContent>
        
        <List sx={{ py: 0 }}>
          {loading ? (
            // Loading skeleton
            Array.from({ length: 11 }).map((_, index) => (
              <Paper 
                key={index}
                elevation={1} 
                sx={{ mx: 2, mb: 1, p: 2 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box sx={{ ml: 2, flex: 1 }}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="40%" />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="circular" width={32} height={32} />
                  </Box>
                </Box>
              </Paper>
            ))
          ) : (
            sportsbookOrder.map((sportsbook, index) => (
            <React.Fragment key={sportsbook.id}>
              <Paper 
                elevation={1} 
                sx={{ 
                  mx: 2, 
                  mb: index === sportsbookOrder.length - 1 ? 2 : 1,
                  transition: 'all 0.2s ease-in-out',
                  backgroundColor: index < 2 ? 'action.hover' : 'background.paper',
                  border: index < 2 ? '1px solid' : 'none',
                  borderColor: index < 2 ? 'primary.main' : 'transparent',
                  '&:hover': {
                    elevation: index < 2 ? 1 : 3,
                    transform: index < 2 ? 'none' : 'translateY(-1px)'
                  }
                }}
              >
                <ListItem sx={{ py: 2 }}>
                  <ListItemIcon>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          minWidth: 24, 
                          textAlign: 'center', 
                          fontWeight: 'bold',
                          color: 'primary.main'
                        }}
                      >
                        {index + 1}
                      </Typography>
                      <SportsIcon color="primary" sx={{ ml: 1 }} />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body1">
                          {sportsbook.name}
                        </Typography>
                        {index < 2 && (
                          <Chip 
                            label="Locked" 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            icon={<LockIcon />}
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        )}
                      </Stack>
                    }
                    secondary={sportsbook.description}
                  />
                  <ListItemSecondaryAction>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => moveUp(index)}
                        disabled={index < 2 || index - 1 < 2}
                        sx={{ 
                          color: (index < 2 || index - 1 < 2) ? 'action.disabled' : 'primary.main' 
                        }}
                      >
                        <ArrowUpIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => moveDown(index)}
                        disabled={index < 2 || index === sportsbookOrder.length - 1}
                        sx={{ 
                          color: (index < 2 || index === sportsbookOrder.length - 1) ? 'action.disabled' : 'primary.main' 
                        }}
                      >
                        <ArrowDownIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        sx={{ 
                          cursor: index < 2 ? 'not-allowed' : 'grab',
                          color: index < 2 ? 'action.disabled' : 'action.active',
                          '&:active': {
                            cursor: index < 2 ? 'not-allowed' : 'grabbing'
                          }
                        }}
                      >
                        <DragHandleIcon />
                      </IconButton>
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>
              </Paper>
            </React.Fragment>
          ))
          )}
        </List>
      </Card>

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reset to Default Order?</DialogTitle>
        <DialogContent>
          <Typography>
            This will reset the sportsbook order back to the original default sequence. 
            You'll need to save your changes to make this permanent.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setResetDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleResetToDefaults} 
            color="primary" 
            variant="contained"
            startIcon={<RestoreIcon />}
          >
            Reset Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Exit Confirmation Dialog */}
      <Dialog
        open={exitConfirmDialogOpen}
        onClose={() => setExitConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved changes to your sportsbook order. 
            What would you like to do?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDiscardAndExit}
            disabled={saving}
          >
            Discard Changes
          </Button>
          <Button 
            onClick={handleSaveAndExit} 
            color="primary" 
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <CheckIcon />}
          >
            {saving ? 'Saving...' : 'Save & Exit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsScreen;
